import { useState, useRef, useEffect } from "react"
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { formatNumber } from "~/ui-rtk/utils/format"
import { MIN_DAILY_BUDGET, MAX_DAILY_BUDGET } from "../types"
import { validateDailyBudget } from "../budget-utils"

interface InlineEditCellProps {
  value: number
  editedValue?: number
  onConfirm: (value: number) => void
}

export default function InlineEditCell({
  value,
  editedValue,
  onConfirm,
}: InlineEditCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const displayValue = editedValue ?? value

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleEdit = () => {
    setInputValue(String(Math.round(displayValue * 100) / 100))
    setError(null)
    setIsEditing(true)
  }

  const handleConfirm = () => {
    const num = parseFloat(inputValue)
    // Validate explicitly and keep the editor open with a message on failure,
    // rather than silently discarding the edit (which reads as "it didn't save").
    const err = validateDailyBudget(num)
    if (err) {
      setError(err)
      return
    }
    setError(null)
    onConfirm(num)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setError(null)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleConfirm()
    if (e.key === "Escape") handleCancel()
  }

  if (isEditing) {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="number"
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={handleKeyDown}
            className={`w-24 px-2 py-1 text-sm bg-basic-black border rounded text-white ${
              error ? "border-states-error" : "border-basic-blue"
            }`}
            step="1"
            min={MIN_DAILY_BUDGET}
            max={MAX_DAILY_BUDGET}
          />
          <button
            onClick={handleConfirm}
            className="p-1 hover:bg-system-blue-800 rounded"
          >
            <CheckIcon className="w-4 h-4 text-american-green" />
          </button>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-system-blue-800 rounded"
          >
            <XMarkIcon className="w-4 h-4 text-states-error" />
          </button>
        </div>
        {error && (
          <span className="text-[10px] text-states-error">{error}</span>
        )}
      </div>
    )
  }

  return (
    <div className="group flex items-center gap-2">
      <span className={editedValue !== undefined ? "text-yellow-300" : ""}>
        {formatNumber(displayValue, { currency: true })}
      </span>
      <button
        onClick={handleEdit}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-system-blue-800 rounded"
      >
        <PencilIcon className="w-3.5 h-3.5 text-gray-400" />
      </button>
    </div>
  )
}
